import React, {
  createRef,
  Fragment,
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { CSSTransition } from 'react-transition-group';
import { useMergedClassNames } from '../../utils/HTMLutils';
import Collapse from '../collapse/Collapse';
import './CalendarEvent.scss';
const rootClassName = 'evento-calendario';

export interface CalendarEventSmall {
  size?: 'small';
  id: number;
  icon?: ReactNode | JSX.Element;
  iconLabel?: never;
  label?: string;
  tipoEvento?: string;
  color?: string;
  borderStyle?: 'solid' | 'dashed';
  width?: never;
  expansible?: never;
  expandedLabel?: never;
  forceExpanded?: never;
  expandedDate?: never;
  disabled?: boolean;
  onClick?: () => void;
}

export interface CalendarEventMedium {
  size?: 'medium';
  id: number;
  icon?: ReactNode | JSX.Element;
  iconLabel?: string;
  label?: string;
  tipoEvento?: string;
  color?: string;
  borderStyle?: 'solid' | 'dashed';
  width?: string;
  expansible?: false;
  expandedLabel?: never;
  forceExpanded?: boolean;
  expandedDate?: never;
  disabled?: boolean;
  onClick?: () => void;
}

export interface CalendarEventMediumExpansible {
  size?: 'medium';
  id: number;
  icon?: ReactNode | JSX.Element;
  iconLabel?: string;
  label?: string;
  tipoEvento?: string;
  color?: string;
  borderStyle?: 'solid' | 'dashed';
  width?: string;
  expansible: true;
  expandedLabel: string;
  forceExpanded?: boolean;
  expandedDate: string;
  disabled?: boolean;
  onClick?: () => void;
}

export interface CalendarEventLarge {
  size?: 'large';
  id: number;
  icon?: ReactNode | JSX.Element;
  iconLabel?: string;
  label?: string;
  tipoEvento?: string;
  color?: string;
  borderStyle?: 'solid' | 'dashed';
  width?: never;
  expansible?: boolean;
  expandedLabel?: string;
  forceExpanded?: boolean;
  expandedDate?: string;
  disabled?: boolean;
  onClick?: () => void;
}

export type CalendarEventProps =
  | CalendarEventSmall
  | CalendarEventMedium
  | CalendarEventMediumExpansible
  | CalendarEventLarge;

function CalendarEvent(props: CalendarEventProps) {
  const [expanded, setExpanded] = useState(props.forceExpanded || false);
  const ref = createRef<HTMLButtonElement>();

  // Window Outside Click
  const outClick = useCallback(
    (evt: MouseEvent) => {
      if (!ref.current) return;
      if (!evt.composedPath().includes(ref.current)) {
        setExpanded(false);
        evt.preventDefault();
      }
    },
    [ref]
  );

  useEffect(() => {
    if (!props.forceExpanded && expanded) {
      window.addEventListener('click', outClick, false);
    }
    return () => {
      if (!props.forceExpanded && expanded) {
        window.removeEventListener('click', outClick, false);
      }
    };
  }, [expanded, outClick, props.forceExpanded]);

  const content = useMemo(() => {
    switch (props.size) {
      case 'large':
        return <Fragment>large</Fragment>;
      case 'medium':
        return (
          <Fragment>
            <div
              className={`${rootClassName}-tipo`}
              style={{ borderColor: props.color }}
            >
              {props.tipoEvento}
            </div>
            <div className={`${rootClassName}-label`}>
              <CSSTransition
                in={!props.forceExpanded && (!expanded || !props.expandedLabel)}
                timeout={300}
                classNames='label'
                unmountOnExit
              >
                <span>{props.label}</span>
              </CSSTransition>
              <Collapse
                opened={
                  props.forceExpanded || (expanded && !!props.expandedLabel)
                }
                animateOpacity
              >
                <Fragment>
                  <span>{props.expandedLabel}</span>
                  <div className={`${rootClassName}-date-label`}>
                    {props.expandedDate}
                  </div>
                </Fragment>
              </Collapse>
            </div>
            <div className={`${rootClassName}-modalidade`}>
              <div className={`${rootClassName}-icon`}>{props.icon}</div>
              <div className={`${rootClassName}-modalidade-label nunito`}>
                {props.iconLabel}
              </div>
            </div>
          </Fragment>
        );
      case 'small':
      default:
        return (
          <Fragment>
            <div className={`${rootClassName}-icon`}>{props.icon}</div>
            <div className={`${rootClassName}-label nunito`}>
              {props.tipoEvento} | {props.label}
            </div>
          </Fragment>
        );
    }
  }, [
    expanded,
    props.color,
    props.expandedDate,
    props.expandedLabel,
    props.forceExpanded,
    props.icon,
    props.iconLabel,
    props.label,
    props.size,
    props.tipoEvento,
  ]);

  return (
    <button
      ref={ref}
      className={useMergedClassNames([
        rootClassName,
        props.size,
        props.borderStyle,
      ])}
      disabled={props.disabled}
      onClick={(evt) => {
        if (typeof props.onClick === 'function') {
          props.onClick();
        }
        if (!props.forceExpanded && props.expansible) {
          setExpanded(!expanded);
        }
        evt.stopPropagation();
      }}
      style={{
        color: props.color,
        borderColor: props.color,
        borderStyle: props.borderStyle,
        width: props.width,
      }}
    >
      {content}
    </button>
  );
}

CalendarEvent.defaultProps = {
  size: 'small',
  color: '#3fa110',
  borderStyle: 'solid',
} as Partial<CalendarEventProps>;

export default memo(CalendarEvent);
